import React, { useCallback } from 'react';
import { Table, Grid, List, Typography, Flex } from 'antd';
import styles from 'components/table/table.module.scss';
import clsx from 'clsx';

type ComponentProps = React.ComponentProps<typeof Table> & {
  styles: {
    [className: string]: string;
  };
};

type ComponentRenderMobileTableProps = React.ComponentProps<typeof Table> & {
  styles: {
    [className: string]: string;
  };
};

export type ColumnsType = React.ComponentProps<typeof Table>['columns'];
export const TableSummary = Table.Summary;

const { Paragraph, Text } = Typography;
const { useBreakpoint } = Grid;

const RenderMobileTable: React.FC<ComponentRenderMobileTableProps> = ({
  dataSource,
  columns,
}: ComponentRenderMobileTableProps) => {
  const renderColumnTitle = useCallback(
    (title: Function | React.ReactNode): React.ReactNode => {
      if (typeof title === 'function') return title();

      return title;
    },
    []
  );

  return (
    <List
      className={clsx(styles.table, styles.mobileTable)}
      dataSource={dataSource as unknown[]}
      renderItem={(item, itemIndex) => {
        return (
          <List.Item key={itemIndex}>
            <div className={styles.tableRow}>
              {columns?.map((column, columnIndex) => {
                if (!column?.render) return null;

                const columnRenderResult = column.render(
                  undefined,
                  item,
                  columnIndex
                );

                if (!React.isValidElement(columnRenderResult)) return null;

                const title = renderColumnTitle(column.title);

                return (
                  <Flex
                    vertical
                    className={styles.mobileListItem}
                    key={columnIndex}
                  >
                    <Text strong>{title}</Text>
                    <Paragraph>{columnRenderResult}</Paragraph>
                  </Flex>
                );
              })}
            </div>
          </List.Item>
        );
      }}
    />
  );
};

const Component: React.FC<ComponentProps> = (tableProps) => {
  const screens = useBreakpoint();
  const isMobile = !screens.lg;

  const tablePropsWithStyledRender = {
    ...tableProps,
    dataSource: tableProps?.dataSource
      ? tableProps.dataSource.map((dataItem) => {
          if (
            typeof dataItem === 'object' &&
            dataItem !== null &&
            !Array.isArray(dataItem)
          )
            return {
              ...dataItem,
              styles: tableProps.styles,
              isMobileMode: isMobile,
            };

          return null;
        })
      : undefined,
  };

  if (isMobile) return <RenderMobileTable {...tablePropsWithStyledRender} />;

  return (
    <Table
      {...tablePropsWithStyledRender}
      className={clsx(styles.table, tablePropsWithStyledRender.className)}
      rowClassName={clsx(tablePropsWithStyledRender.rowClassName)}
    />
  );
};

export default Component;
